// ── BURN RATE — Project storage ─────────────────────────────────────────────
// Persistence layer for saved calculations. Implemented over localStorage today;
// the async ProjectStore interface mirrors a future Supabase-backed store, so
// swapping in the DB later means replacing `localStore` — the UI never changes.

import type { CalcMode } from "./burn-rate";

export interface SavedProject {
  id: string;
  name: string;
  mode: CalcMode;
  snapshot: Record<string, unknown>; // full serialized calculator state
  updatedAt: number;
}

export interface ProjectStore {
  list(): Promise<SavedProject[]>;
  save(input: { id?: string; name: string; mode: CalcMode; snapshot: Record<string, unknown> }): Promise<SavedProject>;
  remove(id: string): Promise<void>;
}

const KEY = "burnrate_projects_v1";
const uid = () => Math.random().toString(36).slice(2, 9);

function read(): SavedProject[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SavedProject[]) : [];
  } catch {
    return [];
  }
}

function write(list: SavedProject[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* quota or disabled — fail silently */
  }
}

// ── localStorage implementation ───────────────────────────────────────────────
// TODO(account): replace with a SupabaseProjectStore implementing ProjectStore.

export const localStore: ProjectStore = {
  async list() {
    return read().sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async save({ id, name, mode, snapshot }) {
    const list = read();
    const now = Date.now();
    const existingIdx = id ? list.findIndex((p) => p.id === id) : -1;

    if (existingIdx >= 0) {
      const updated: SavedProject = { ...list[existingIdx], name, mode, snapshot, updatedAt: now };
      list[existingIdx] = updated;
      write(list);
      return updated;
    }

    const created: SavedProject = { id: uid(), name, mode, snapshot, updatedAt: now };
    write([created, ...list]);
    return created;
  },

  async remove(id) {
    write(read().filter((p) => p.id !== id));
  },
};

// ── Insumos library ───────────────────────────────────────────────────────────
// Reusable catalog of ingredients / materials with their saved unit cost, so the
// user types a price once and reuses it across recipes and products.

export interface SavedInsumo {
  id: string;
  name: string;
  unitCost: string;
  updatedAt: number;
}

export interface InsumoStore {
  list(): Promise<SavedInsumo[]>;
  save(input: { name: string; unitCost: string }): Promise<SavedInsumo | null>;
  remove(id: string): Promise<void>;
}

const INSUMO_KEY = "burnrate_insumos_v1";

function readInsumos(): SavedInsumo[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(INSUMO_KEY);
    return raw ? (JSON.parse(raw) as SavedInsumo[]) : [];
  } catch {
    return [];
  }
}

function writeInsumos(list: SavedInsumo[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(INSUMO_KEY, JSON.stringify(list));
  } catch {
    /* quota or disabled */
  }
}

// TODO(account): replace with a SupabaseInsumoStore implementing InsumoStore.
export const localInsumoStore: InsumoStore = {
  async list() {
    return readInsumos().sort((a, b) => a.name.localeCompare(b.name));
  },

  async save({ name, unitCost }) {
    const clean = name.trim();
    if (!clean) return null; // don't save nameless insumos
    const list = readInsumos();
    const now = Date.now();
    const idx = list.findIndex((i) => i.name.toLowerCase() === clean.toLowerCase());

    if (idx >= 0) {
      const updated: SavedInsumo = { ...list[idx], name: clean, unitCost, updatedAt: now };
      list[idx] = updated;
      writeInsumos(list);
      return updated;
    }

    const created: SavedInsumo = { id: uid(), name: clean, unitCost, updatedAt: now };
    writeInsumos([created, ...list]);
    return created;
  },

  async remove(id) {
    writeInsumos(readInsumos().filter((i) => i.id !== id));
  },
};
